// ======================================================================
// File Name       : GroupAction
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import * as groupRequests from './httpRequests/group';
import * as userRequests from './httpRequests/user';
import * as userAttributeRequests from './httpRequests/userAttribute';
import * as authorityActions from './AuthorityAction';
import * as modalActions from './ModalAction';
import * as userAttributeActions from './UserAttributeAction';
import * as ActionTypes from '../constants/actionTypes/group';
import * as ModalIds from '../constants/ModalIds';
import { MODE } from '../constants';
import { USER_ATTRIBUTE_TYPE } from '../constants/UserAttributeConstants';
import { USER_CREATE_TYPE, USER_TYPE } from '../constants/UserConstants';

export function fetchGroupStructure(isGetAll) {
  return dispatch =>
    groupRequests
      .postGroupGetStructure(undefined, isGetAll)
      .then(({ groupList }) => {
        return dispatch(doReceiveGroupStructureList(groupList));
      });
}

export function exportCSV() {
  return (dispatch) => {
    groupRequests.exportCSV();
  };
}

export function deleteGroup(groupSecernId) {
  return dispatch =>
    groupRequests.postGroupEdit(MODE.DELETE_MODE, { groupSecernId }).then(() => {
      dispatch(fetchGroupStructure());
    });
}

export function openGroupRegisterDialog(parentGroupSecernId) {
  return (dispatch) => {
    dispatch(authorityActions.getAuthorityList());
    dispatch(doInitGroupEditing({ parentGroupSecernId }));
    dispatch(userAttributeActions.initUserAttributeEditing());
    dispatch(modalActions.openModal(ModalIds.GROUP_REGISTER_EDIT));
  };
}

export function openGroupDetailDialog(groupSecernId) {
  return (dispatch) => {
    // Reset user attribute and group on Store
    dispatch(userAttributeActions.doReceiveUserAttributeList([]));
    dispatch(userAttributeActions.doInitUserAttributeEditing());
    dispatch(resetInitData());

    dispatch(modalActions.openModal(ModalIds.GROUP_DETAIL));

    const requests = [];
    // fetch authority info
    requests.push(dispatch(authorityActions.getAuthorityList()));
    // fetch user attribute
    requests.push(
      dispatch(
        userAttributeActions.initUserAttributeEditing(USER_ATTRIBUTE_TYPE.GROUP, groupSecernId),
      ),
    );
    // fetch user group -> fetch user info
    const userGroupRequest = groupRequests
      .postUserGroupRelationGetList({
        searchInfo: { groupList: [groupSecernId] },
      })
      .then((response) => {
        const { groupRelationList, recordNum } = response;
        const userSecernIdList = groupRelationList.map(cur => cur.userSecernId);
        if (userSecernIdList.length) {
          return userRequests.postUserGetList({ searchInfo: { userSecernIdList, userCreateType: USER_CREATE_TYPE.DEFINED_AND_REPRESENTATIVE_USER } })
            .then(({ userList = [] }) => dispatch(doReceiveUserGroup(userList, recordNum)));
        }
        return {};
      });

    // push to requests
    requests.push(userGroupRequest);

    Promise.all(requests).then(() => {
      dispatch(doInitGroupEditing({ groupSecernId }));
    });
  };
}

export function editingGroup(payload) {
  return doReceiveGroupEditing(payload);
}

export function editingAdminSearchCondition(payload) {
  return doReceiveGroupUserAdminSearchCondition(payload);
}

export function searchUserAdmin({
  startPosition,
  returnMaxNum,
  userAttributeKeyNum,
  userAttributeSearchText,
  managementAuthoritySecernId,
  userName,
  loginId,
}) {
  return (dispatch) => {
    let searchByUserAtribute = (async () => [])();
    if (userAttributeSearchText) {
      searchByUserAtribute = userAttributeRequests
        .postUserAttributeGetList({
          searchInfo: {
            userAttributeType: USER_ATTRIBUTE_TYPE.USER,
            userAttributeKeyNum,
            userAttributeSearchText,
          },
        })
        .then(({ userAttributeList }) => userAttributeList.map(cur => cur.secernId));
    }

    return searchByUserAtribute.then((userSecernIdList) => {
      if (userAttributeSearchText && (!userSecernIdList || !userSecernIdList.length)) {
        dispatch(doReceiveGroupUserAdminSearchResult(startPosition, 0, []));
      } else {
        return userRequests.postUserGetList({
          searchInfo: {
            userType: USER_TYPE.ADMIN,
            managementAuthorityList: managementAuthoritySecernId ? [managementAuthoritySecernId] : null,
            userName,
            loginId,
            userSecernIdList,
            startPosition,
            returnMaxNum,
            userCreateType: USER_CREATE_TYPE.REPRESENTATIVE_USER,
          },
        }).then(({ recordNum, userList }) => {
          dispatch(doReceiveGroupUserAdminSearchResult(startPosition, recordNum, userList));
        });
      }
    });
  };
}

export function editingUserAdmin({ removedSecern, addedList = [], removedAll }) {
  return doReceiveGroupUserAdminEditing(removedSecern, addedList, removedAll);
}

export function editingNotAdminSearchCondition(payload) {
  return doReceiveGroupUserNotAdminSearchCondition(payload);
}

export function searchUserNotAdmin({
  startPosition,
  returnMaxNum,
  userAttributeKeyNum,
  userAttributeSearchText,
  userType,
  userName,
  loginId,
}) {
  return (dispatch) => {
    let searchByUserAtribute = (async () => [])();
    if (userAttributeSearchText) {
      searchByUserAtribute = userAttributeRequests
        .postUserAttributeGetList({
          searchInfo: {
            userAttributeType: USER_ATTRIBUTE_TYPE.USER,
            userAttributeKeyNum,
            userAttributeSearchText,
          },
        })
        .then(({ userAttributeList }) => userAttributeList.map(cur => cur.secernId));
    }

    return searchByUserAtribute.then((userSecernIdList) => {
      if (userAttributeSearchText && (!userSecernIdList || !userSecernIdList.length)) {
        dispatch(doReceiveGroupUserNotAdminSearchResult(startPosition, 0, []));
      } else {
        return userRequests.postUserGetList({
          searchInfo: {
            userType,
            userName,
            loginId,
            userSecernIdList,
            startPosition,
            returnMaxNum,
          },
        }).then(({ recordNum, userList }) => {
          dispatch(doReceiveGroupUserNotAdminSearchResult(startPosition, recordNum, userList));
        });
      }
    });
  };
}

export function editingUserNotAdmin({ removedSecern, addedList = [], removedAll }) {
  return doReceiveGroupUserNotAdminEditing(removedSecern, addedList, removedAll);
}

export function saveGroup() {
  return (dispatch, getState) => {
    const { group } = getState();
    const { groupName, parentGroupSecernId } = group.editingGroup;
    // Save group's information
    return groupRequests
      .postGroupEdit(group.editType, {
        groupSecernId: group.editingGroup.groupSecernId,
        groupName,
        parentGroupSecernId: group.editType !== MODE.EDIT_MODE ? parentGroupSecernId : null,
      })
      .then(({ groupSecernId }) => {
        const requests = [];
        // user atribute
        const { editingUserAtribute } = getState().userAttribute;
        requests.push(
          dispatch(
            userAttributeActions.saveUserAttribute(USER_ATTRIBUTE_TYPE.GROUP, groupSecernId, group.editType),
          ),
        );
        // user group
        const { storedUserGroup, editingUserGroupAdmin, editingUserGroupNotAdmin } = group;
        const edittingUsers = { ...editingUserGroupAdmin, ...editingUserGroupNotAdmin };
        // deleted user
        const deletedUsers = Object.keys(storedUserGroup).reduce((acc, userSecernId) => {
          if (!edittingUsers[userSecernId]) {
            acc.push(userSecernId);
          }
          return acc;
        }, []);
        const registeredUsers = Object.keys(edittingUsers).reduce((acc, userSecernId) => {
          if (!storedUserGroup[userSecernId]) {
            acc.push(userSecernId);
          }
          return acc;
        }, []);

        return new Promise((resolve, reject) => {
          if (!registeredUsers || !registeredUsers.length) {
            resolve();
            return {};
          }
          return groupRequests
            .postUserGroupRelationEdit(MODE.NEW_MODE, groupSecernId, registeredUsers)
            .then(() => {
              resolve();
            });
        })
          .then(
          () =>
            new Promise((resolve, reject) => {
              if (!deletedUsers || !deletedUsers.length) {
                resolve();
                return {};
              }
              return groupRequests
                .postUserGroupRelationEdit(MODE.DELETE_MODE, groupSecernId, deletedUsers)
                .then(() => {
                  resolve();
                });
            }),
        )
          .then(() => {
            dispatch(modalActions.hideModal());
            return dispatch(fetchGroupStructure());
          });
      });
  };
}

export function selectedGroupOnDialog(selectedTargetGroup) {
  return (dispatch) => {
    dispatch(doSelectedGroupOnDialog(selectedTargetGroup));
  };
}

export function selectedGroupIdsOnDialog(selectedTargetGroupIds) {
  return dispatch => dispatch(doSelectedGroupIdsOnDialog(selectedTargetGroupIds));
}

function doSelectedGroupOnDialog(selectedTargetGroup) {
  return {
    type: ActionTypes.SELECTED_GROUP_ON_DIALOG,
    selectedTargetGroup,
  };
}

function doSelectedGroupIdsOnDialog(selectedTargetGroupIds) {
  return {
    type: ActionTypes.SELECTED_GROUP_IDS_ON_DIALOG,
    selectedTargetGroupIds,
  };
}

function doReceiveGroupStructureList(structureList) {
  return {
    type: ActionTypes.GROUP_RECEIVE_STRUCTURE_LIST,
    structureList,
  };
}

function doInitGroupEditing({ groupSecernId, parentGroupSecernId } = {}) {
  return {
    type: ActionTypes.GROUP_INIT_EDITING,
    groupSecernId,
    parentGroupSecernId,
  };
}

export function resetInitData() {
  return {
    type: ActionTypes.GROUP_RESET_INIT_DATA,
  };
}

function doReceiveGroupEditing(payload) {
  return {
    type: ActionTypes.GROUP_RECEIVE_EDITING_GROUP,
    payload,
  };
}

function doReceiveUserGroup(userList, recordNum) {
  return {
    type: ActionTypes.GROUP_RECEIVE_USER_GROUP,
    userList,
    recordNumUserInGroup: recordNum,
  };
}

function doReceiveGroupUserAdminEditing(removedSecern, addedList = [], removedAll) {
  return {
    type: ActionTypes.GROUP_RECEIVE_EDITING_USER_ADMIN,
    removedSecern,
    addedList,
    removedAll,
  };
}

function doReceiveGroupUserAdminSearchCondition(payload) {
  return {
    type: ActionTypes.GROUP_RECEIVE_SEARCH_CONDITION_ADMIN,
    payload,
  };
}

function doReceiveGroupUserAdminSearchResult(startPosition, recordNum, userList) {
  return {
    type: ActionTypes.GROUP_RECEIVE_SEARCH_RESULT_ADMIN,
    startPosition,
    recordNum,
    userList,
  };
}

function doReceiveGroupUserNotAdminEditing(removedSecern, addedList = [], removedAll) {
  return {
    type: ActionTypes.GROUP_RECEIVE_EDITING_USER_NOT_ADMIN,
    removedSecern,
    addedList,
    removedAll,
  };
}

function doReceiveGroupUserNotAdminSearchCondition(payload) {
  return {
    type: ActionTypes.GROUP_RECEIVE_SEARCH_CONDITION_NOT_ADMIN,
    payload,
  };
}

function doReceiveGroupUserNotAdminSearchResult(startPosition, recordNum, userList) {
  return {
    type: ActionTypes.GROUP_RECEIVE_SEARCH_RESULT_NOT_ADMIN,
    startPosition,
    recordNum,
    userList,
  };
}

function doReceivedUserIdFromGroupId(userListFromGroupList) {
  return {
    type: ActionTypes.RECEIVED_USER_ID_LIST,
    userListFromGroupList,
  };
}

export function getUserIdFromGroupId(groupList) {
  const params = {
    searchInfo: {
      groupList,
      startPosition: 0,
      returnMaxNum: 1000,
    },
  };
  return groupRequests.postUserGroupRelationGetList(params).then((result) => {
    const listLength = result.groupRelationList.length;
    const userIdList = [];
    for (let i = 0; i < listLength; i++) {
      userIdList.push(result.groupRelationList[i].userSecernId);
    }
    // resolve(userIdList);
    return userIdList;
  });
}

export function removeSelectedGroup() {
  return {
    type: ActionTypes.REMOVED_SELECTED_GROUP,
  };
}

export function removeSelectedGroupIds() {
  return {
    type: ActionTypes.REMOVED_SELECTED_GROUP_IDS,
  };
}

export function removeGroupList() {
  return {
    type: ActionTypes.REMOVE_GROUP_LIST,
  };
}
